import { Injectable, signal } from '@angular/core';

/**
 * `sessionStorage` key holding the count of consecutive auto-reloads triggered by
 * chunk-load recovery. Persisted (not in memory) so the cap survives the reloads
 * it is meant to bound.
 */
const RELOAD_ATTEMPTS_KEY = 'chunkReloadAttempts';

/**
 * Max consecutive auto-reloads before recovery gives up and shows the manual
 * fallback, so a genuinely broken deploy (not just a stale tab) can't loop.
 */
const MAX_RELOAD_ATTEMPTS = 2;

/**
 * Signatures of a lazy-chunk / dynamic-import load failure, across browsers and
 * Angular builds: classic `ChunkLoadError`, native dynamic-import fetch failures,
 * cross-build export mismatches, and strict-MIME module-script rejections.
 */
const CHUNK_ERROR_PATTERN =
  /ChunkLoadError|Loading chunk [\w-]+ failed|Failed to fetch dynamically imported module|error loading dynamically imported module|does not provide an export named|Importing a module script failed|Failed to load module script/i;

/**
 * Central coordinator for recovering from lazy-chunk load failures (see the UAT
 * chunk-load issue). Detects chunk errors, hard-reloads a bounded number of times
 * to pull a consistent build from the server, and — once the cap is hit — signals
 * the shell to show a manual reload fallback instead of looping.
 */
@Injectable({ providedIn: 'root' })
export class ChunkRecoveryService {
  /** True once auto-reload attempts are exhausted and manual reload is required. */
  private readonly loadFailedSignal = signal(false);

  /**
   * Latch ensuring a single document triggers at most one recovery, even when
   * several chunk errors fire — or the navigation-error and global-error paths
   * both fire for the same failure. Naturally resets on the full reload.
   */
  private recovering = false;

  /** True when chunk recovery has given up; the shell should show the manual fallback. */
  public readonly loadFailed = this.loadFailedSignal.asReadonly();

  /**
   * Whether an error looks like a lazy-chunk / dynamic-import load failure.
   * @param error the thrown error, promise rejection, or NavigationError payload
   * @returns true when the error matches a known chunk-load failure signature
   */
  public isChunkLoadError(error: unknown): boolean {
    const err = error as { name?: string; message?: string; rejection?: { message?: string } } | null;
    if (err?.name === 'ChunkLoadError') {
      return true;
    }
    const haystack = [err?.name, err?.message, err?.rejection?.message, typeof error === 'string' ? error : '']
      .filter(Boolean)
      .join(' ');
    return CHUNK_ERROR_PATTERN.test(haystack);
  }

  /**
   * Recovers from a chunk-load failure by hard-navigating to `targetUrl` (default:
   * the current URL), which refetches index.html and the current build's chunks.
   * Bounded by a persisted attempt counter; once exhausted it surfaces the manual
   * fallback via {@link loadFailed$} instead of reloading again.
   * @param targetUrl the URL to land on after reload; defaults to the current location
   */
  public recover(targetUrl?: string): void {
    if (this.recovering) {
      return;
    }
    this.recovering = true;
    const attempts = this.readAttempts();
    if (attempts >= MAX_RELOAD_ATTEMPTS) {
      this.loadFailedSignal.set(true);
      return;
    }
    this.writeAttempts(attempts + 1);
    this.navigateTo(targetUrl || window.location.pathname + window.location.search);
  }

  /**
   * Clears the auto-reload attempt counter and hides the fallback. Called after a
   * navigation succeeds, confirming chunks now load cleanly.
   */
  public reset(): void {
    sessionStorage.removeItem(RELOAD_ATTEMPTS_KEY);
    this.loadFailedSignal.set(false);
  }

  /** Clears recovery state and performs a manual full reload (fallback UI action). */
  public reloadNow(): void {
    this.reset();
    this.doReload();
  }

  /**
   * Reads the persisted consecutive-attempt count.
   * @returns the current attempt count (0 when unset or unparseable)
   */
  private readAttempts(): number {
    return Number(sessionStorage.getItem(RELOAD_ATTEMPTS_KEY) ?? '0') || 0;
  }

  /**
   * Persists the consecutive-attempt count.
   * @param value the count to store
   */
  private writeAttempts(value: number): void {
    sessionStorage.setItem(RELOAD_ATTEMPTS_KEY, String(value));
  }

  /**
   * Hard-navigates the browser to a URL. Isolated for testability.
   * @param url the URL to navigate to
   */
  private navigateTo(url: string): void {
    window.location.assign(url);
  }

  /** Reloads the current document. Isolated for testability. */
  private doReload(): void {
    window.location.reload();
  }
}
